import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './ScreenshotCarousel.css';
import ScreenshotModal from './ScreenshotModal';

const ScreenshotCarousel = ({ screenshots }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalSlide, setModalSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Carousel navigation
  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % screenshots.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + screenshots.length) % screenshots.length);
  };

  // Modal handlers
  const openModal = (index) => {
    setModalSlide(index);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const modalNextSlide = () => {
    setModalSlide((prev) => (prev + 1) % screenshots.length);
  };

  const modalPrevSlide = () => {
    setModalSlide((prev) => (prev - 1 + screenshots.length) % screenshots.length);
  };

  const goToModalSlide = (index) => {
    setModalSlide(index);
  };

  // Touch handlers for swipe navigation
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };

  return (
    <div className="screenshots-carousel">
      {/* Desktop navigation - left button */}
      <button className="carousel-button carousel-button-prev carousel-button-desktop" onClick={prevSlide} aria-label="Previous screenshot">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      <div className="carousel-content-wrapper">
        <div className="carousel-container">
          <div
            className="carousel-slides"
            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {screenshots.map((screenshot, index) => (
              <div key={index} className="carousel-slide">
                <img
                  src={screenshot.src}
                  alt={screenshot.title}
                  className="carousel-image"
                  onClick={() => openModal(index)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      openModal(index);
                    }
                  }}
                />
                <div
                  className="carousel-caption"
                  onClick={() => openModal(index)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      openModal(index);
                    }
                  }}
                >
                  <h3 className="carousel-title">{screenshot.title}</h3>
                  <p className="carousel-description">{screenshot.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="carousel-dots">
            {screenshots.map((_, index) => (
              <button
                key={index}
                className={`carousel-dot ${index === currentSlide ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to screenshot ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Mobile navigation buttons */}
        <div className="carousel-mobile-nav">
          <button className="carousel-button carousel-button-prev" onClick={prevSlide} aria-label="Previous screenshot">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button className="carousel-button carousel-button-next" onClick={nextSlide} aria-label="Next screenshot">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Desktop navigation - right button */}
      <button className="carousel-button carousel-button-next carousel-button-desktop" onClick={nextSlide} aria-label="Next screenshot">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {/* Screenshot Modal */}
      <ScreenshotModal
        isOpen={isModalOpen}
        screenshots={screenshots}
        currentIndex={modalSlide}
        onClose={closeModal}
        onNext={modalNextSlide}
        onPrev={modalPrevSlide}
        onGoToSlide={goToModalSlide}
      />
    </div>
  );
};

ScreenshotCarousel.propTypes = {
  screenshots: PropTypes.arrayOf(PropTypes.shape({
    src: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  })).isRequired,
};

export default ScreenshotCarousel;
